import type {
  IRealtimeEntity,
  RealtimeEntityPropertyValue,
} from '@interfaces/realtimeEntities';

// What a cleared link sends. Presence decides whether the engine applies a
// field, so an omitted key means "leave alone" and cannot express "no target";
// the empty string is the value that can.
export const OBJECT_REFERENCE_NONE = '';

/**
 * Reads a stored property value as the id of a linked entity, trimmed.
 * Anything but a non-empty string reads as no link.
 *
 * @param value - The value held for an object property, possibly absent.
 * @returns The target's entity id, or an empty string.
 */
export const readObjectReference = (
  value: RealtimeEntityPropertyValue | undefined,
): string => (typeof value === 'string' ? value.trim() : '');

/**
 * Lists the entities an object property may be pointed at: every other entity
 * in the session registry.
 *
 * Deliberately not narrowed by type. The build exports an object slot as bare
 * `type: "object"`, and what a slot accepts is the engine's call, not the
 * web's — a guess here would only ever hide a target the engine would have
 * taken. The one exclusion is the entity being edited: an object linking to
 * itself is never what an author means.
 *
 * @param entities - The session registry of spawned entities.
 * @param ownerEntityId - The entity whose property is being edited.
 * @returns The candidate targets, in registry order.
 */
export const collectObjectPropertyCandidates = (
  entities: IRealtimeEntity[],
  ownerEntityId: string,
): IRealtimeEntity[] =>
  entities.filter((entity) => entity.entityId !== ownerEntityId);

/** One entity holding links to a target, and the properties that hold them. */
export interface ObjectPropertyReferrer {
  entity: IRealtimeEntity;
  propertyNames: string[];
}

/**
 * Finds every entity linking to a target, and which of their properties do it.
 *
 * A property is a link when its value is the target's id — matched on the
 * value alone, not on the build's schema, which is exactly what the backend
 * does when it rewrites links on a branding copy. The schema would be the
 * precise source, but the streamed build's record blanks during a refresh and
 * a delete in that window would then leave every link standing; ids are v4
 * UUIDs, so a string property colliding with one is not a case worth guarding.
 *
 * @param entities - The session registry of spawned entities.
 * @param targetEntityId - The entity being linked to.
 * @returns One entry per linking entity; empty when nothing links to it.
 */
export const collectObjectReferrers = (
  entities: IRealtimeEntity[],
  targetEntityId: string,
): ObjectPropertyReferrer[] => {
  const referrers: ObjectPropertyReferrer[] = [];

  for (const entity of entities) {
    if (entity.entityId === targetEntityId || !entity.properties) {
      continue;
    }

    const propertyNames = Object.keys(entity.properties).filter(
      (name) =>
        readObjectReference(entity.properties?.[name]) === targetEntityId,
    );

    if (propertyNames.length) {
      referrers.push({ entity, propertyNames });
    }
  }

  return referrers;
};
